import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Drawer,
  Button,
  Col,
  Row,
  Divider,
  Space,
  Avatar,
  Typography,
  Switch,
  notification,
} from 'antd';
import { ShareAltOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';

import mixpanel from 'mixpanel-browser';
import { version } from '../../../package.json';
import { NotifContext } from '../Context/NotifContext';
import { ThemeContext } from '../Context/ThemeContext';
import { ProfileContext } from '../Context/ProfileContext';
import ProfileDrawerFooter from '../Footer/ProfileDrawerFooter';
import LoginButton from '../Button/LoginButton';
import LogoutButton from '../Button/LogoutButton';
import * as Config from '../../Config';
import ShowUsersDrawer from './ViewUsers';
import * as pwa from '../../installPWA';
import HelpCenter from './HelpCenter';

const { Text } = Typography;

const ProfileDrawer = () => {
  const history = useHistory();
  const {
    profile,
    showProfile,
    closeProfileDrawer,
    updateSettings,
    updateSettingsLoader,
    openHelpCenter,
  } = useContext(ProfileContext);
  const { theme, changeTheme, updateAutoTheming } = useContext(ThemeContext);
  const { show } = useContext(NotifContext);
  const loggedin = localStorage.getItem('loggedin') === 'true';
  const showShareSection = !!navigator.share;
  const [checkingVersion, setCheckingVersion] = useState(false);

  const handleCheckVersion = async () => {
    setCheckingVersion(true);
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`);
      const result = await response.json();
      const serverVersion = result.version;

      if (!serverVersion) {
        notification.warning({ message: 'Unable to check version' });
        return;
      }

      if (serverVersion !== version) {
        notification.info({
          message: 'Update available',
          description: `v${serverVersion} is ready. Clearing cache and reloading...`,
        });
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
        }
        window.location.reload();
      } else {
        notification.success({
          message: 'Already up to date',
          description: `Running v${version}`,
        });
      }
    } catch (err) {
      notification.error({
        message: "Couldn't check for updates",
        description: err.message,
      });
    } finally {
      setCheckingVersion(false);
    }
  };
  // Must be triggered some kind of "user activation"
  const share = async () => {
    const shareData = {
      title: `${profile.userData.user.name.fullName} is Interact`,
      text: `${profile.userData.user.name.fullName} invited you to see his interact updates on Interact.`,
      url: `${Config.app}/${profile.userData.uid}`,
    };
    mixpanel.track('Share Button Clicked', shareData);
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        mixpanel.track('Share Successfully');
        show('success', 'Share Notification', 'Shared Successfully');
      } catch (err) {
        mixpanel.track('Share unsuccessfully');
        show(
          'error',
          'Share Notification',
          'There was an error while sharing!'
        );
      }
    }
  };

  return (
    <div>
      <Drawer
        title={<Text style={{ color: theme.drawer.text }}>Your Profile</Text>}
        width={theme.isMobile ? '100%' : '440px'}
        // height={'90%'}
        onClose={closeProfileDrawer}
        visible={showProfile}
        placement='right'
        headerStyle={{
          background: theme.drawer.bg,
        }}
        bodyStyle={{
          background: theme.drawer.bg,
        }}
        footerStyle={{
          background: theme.drawer.bg,
        }}
        footer={
          <div
            style={{
              textAlign: 'right',
            }}
          >
            <Button onClick={closeProfileDrawer} style={{ marginRight: 8 }}>
              Close
            </Button>
          </div>
        }
      >
        {/* Avatar */}
        <Row align='center'>
          <Avatar
            shape='square'
            size={192}
            icon={
              <img
                src={profile.userData.user.picture.replace(
                  's96-c',
                  's512-c',
                  true
                )}
                alt={profile.userData.user.name.fullName}
              />
            }
          />
          <Text
            style={{
              fontSize: 'x-small',
              padding: '0.5rem',
              color: theme.drawer.secondaryText,
            }}
          >
            We don't support profile picture updates yet. If you want to change
            your profile picture please change your google profile picture.
          </Text>
        </Row>

        {/* All about you */}
        <Divider style={{ color: theme.drawer.text }}>All about you</Divider>
        <Row>
          <Space>
            <Col style={{ color: theme.drawer.text }}>Hello</Col>
            <Col>
              <Text style={{ color: theme.drawer.text }} strong>
                {profile.userData.user.name.fullName}
              </Text>
            </Col>
          </Space>
        </Row>
        <Row>
          <Space>
            <Col style={{ color: theme.drawer.text }}>You've posted&nbsp;</Col>
            <Col style={{ color: theme.drawer.text }}>
              <Text style={{ color: theme.drawer.text }} strong>
                {profile.total_updates}
              </Text>
            </Col>
            <Col style={{ color: theme.drawer.text }}>
              {profile.total_updates === 1 ? 'update' : 'updates'}
            </Col>
          </Space>
        </Row>

        {/* Installation */}
        <Row className='install-app-container hidden'>
          <Col span={24}>
            <Divider style={{ color: theme.drawer.text }}>Installation</Divider>
          </Col>
          <Col span={24}>
            <Space direction='vertical'>
              <Text
                type='secondary'
                style={{
                  fontSize: 'small',
                  color: theme.drawer.secondaryText,
                }}
              >
                You don't need to remember and type the url next time, just
                download the app
              </Text>
              <Col xs={24}>
                <Button
                  className='install-app-button'
                  block
                  size='medium'
                  type='primary'
                  onClick={pwa.install}
                >
                  Install <DownloadOutlined />
                </Button>
              </Col>
            </Space>
          </Col>
        </Row>

        {/* Show Community */}
        {loggedin ? (
          <div>
            <Divider style={{ color: theme.drawer.text }}>Community</Divider>
            {/* <Row>
                            <Space>
                                <Col xs={20}>
                                    <Text
                                        style={{
                                            fontSize: 'small',
                                            color: theme.drawer.text,
                                        }}
                                    >
                                        You can view and unfollow people you are
                                        following
                                    </Text>
                                </Col>
                                <Col xs={4} align='right'>
                                    <Button
                                        size='small'
                                        type='primary'
                                        onClick={openUsersDrawer}
                                    >
                                        Go <ArrowRightOutlined />
                                    </Button>
                                </Col>
                            </Space>
                        </Row>
                        <br /> */}
            <Row>
              <Col xs={24}>
                <Button
                  block
                  size='medium'
                  type='primary'
                  onClick={() => {
                    history.push('/explore');
                  }}
                >
                  Explore People
                </Button>
              </Col>
            </Row>
            <ShowUsersDrawer />
          </div>
        ) : null}

        {/* Settings */}
        <Divider style={{ color: theme.drawer.text }}>Settings</Divider>
        {/* Auto Theme */}
        <Row style={{ marginTop: '0.5rem' }}>
          <Col xs={20}>
            <Text style={{ color: theme.drawer.text }} strong>
              Auto Theme
            </Text>
          </Col>
          <Col xs={4} style={{ color: theme.header.text, textAlign: 'right' }}>
            <Switch
              checked={theme.isThemeAuto}
              onChange={(checked) => {
                updateAutoTheming(checked ? 'auto' : 'not_auto');
              }}
            />
          </Col>
          <Text
            type='secondary'
            style={{
              fontSize: 'small',
              color: theme.drawer.secondaryText,
            }}
          >
            {theme.isThemeAuto
              ? 'You have auto theming active, toggle to switch to custom mode'
              : 'Enable it to get dyanmic themes at day and night'}
          </Text>
        </Row>

        {/* Dark Mode */}
        <Row style={{ marginTop: '0.5rem' }}>
          <Col xs={20}>
            <Text style={{ color: theme.drawer.text }} strong>
              Dark Mode
            </Text>
          </Col>
          <Col xs={4} style={{ color: theme.header.text, textAlign: 'right' }}>
            <Switch
              disabled={theme.isThemeAuto}
              checked={!!(theme.label && theme.label === 'Dark')}
              onChange={(checked) => {
                changeTheme(checked ? 'dark' : 'light');
              }}
            />
          </Col>
          <Text
            type='secondary'
            style={{
              fontSize: 'small',
              color: theme.drawer.secondaryText,
            }}
          >
            {theme.label === 'Dark'
              ? 'You have Dark mode active, toggle to switch to Light mode'
              : 'You have Light mode active, toggle to switch to Dark mode'}
          </Text>
        </Row>

        {/* Privacy */}
        <Row style={{ marginTop: '0.5rem' }} hidden={!loggedin}>
          <Col xs={20}>
            <Text style={{ color: theme.drawer.text }} strong>
              Private Account
            </Text>
          </Col>
          <Col xs={4} style={{ color: theme.header.text, textAlign: 'right' }}>
            <Switch
              loading={updateSettingsLoader === 'private'}
              checked={Boolean(profile.userData.settings.private)}
              onChange={(checked) => {
                updateSettings('private', !!checked);
              }}
            />
          </Col>
          <Text
            type='secondary'
            style={{
              fontSize: 'small',
              color: theme.drawer.secondaryText,
            }}
          >
            {profile.userData.settings.private
              ? 'Your updates are only visible to you'
              : 'Your updates are visible to everyone'}
          </Text>
        </Row>

        {/* search options */}
        <Row style={{ marginTop: '0.5rem' }} hidden={!loggedin}>
          <Col xs={20}>
            <Text style={{ color: theme.drawer.text }} strong>
              Seachable Profile
            </Text>
          </Col>
          <Col xs={4} style={{ color: theme.header.text, textAlign: 'right' }}>
            <Switch
              loading={updateSettingsLoader === 'searchable_profile'}
              checked={Boolean(profile.userData.settings.searchable_profile)}
              onChange={(checked) => {
                updateSettings('searchable_profile', !!checked);
              }}
            />
          </Col>
          <Text
            type='secondary'
            style={{
              fontSize: 'small',
              color: theme.drawer.secondaryText,
            }}
          >
            {profile.userData.settings.searchable_profile
              ? 'Users on the platform can search for your profile and view your basic information like name, profile picture and number of updates'
              : 'Your profile will not be discoverable on the search page'}
          </Text>
        </Row>

        {/* Help Center */}
        {loggedin ? (
          <div>
            <Divider style={{ color: theme.drawer.text }}>Help Center</Divider>
            <Row>
              <Col xs={24}>
                <Button
                  block
                  size='medium'
                  type='primary'
                  onClick={openHelpCenter}
                >
                  Open
                </Button>
              </Col>
            </Row>
            <HelpCenter />
          </div>
        ) : null}

        {/* Your account */}
        <Divider style={{ color: theme.drawer.text }}>Your Account</Divider>
        {loggedin ? (
          <Row>
            <Col xs={24} style={{ color: theme.drawer.text }}>
              Your registered email -{' '}
              <Text style={{ color: theme.drawer.secondaryText }} disabled>
                {profile.userData.user.email}
              </Text>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={24} style={{ color: theme.drawer.text }}>
            {loggedin ? (
              <Text style={{ color: theme.drawer.text }}>
                You're a member since&nbsp;
                <Text style={{ color: theme.drawer.text }} strong>
                  {new Date(profile.userData.user.createdAt).toDateString()}
                </Text>
              </Text>
            ) : (
              <Text style={{ color: theme.drawer.text }}>
                You don't have an account yet! <br />
                Create a new account or login to your existing account.
              </Text>
            )}
          </Col>
        </Row>

        {/* Share and Invite */}
        <Divider
          hidden={!loggedin || !showShareSection}
          style={{ color: theme.drawer.text }}
        >
          Share & Invite
        </Divider>
        <Row hidden={!loggedin || !showShareSection}>
          <Col xs={20}>
            <Text style={{ color: theme.drawer.text }} strong>
              Share
            </Text>
          </Col>
          <Col xs={4} className='profile-share-btn'>
            <ShareAltOutlined
              onClick={share}
              style={{
                color: theme.drawer.text,
                cursor: 'pointer',
              }}
            />
          </Col>
          <Text
            type='secondary'
            style={{
              fontSize: 'small',
              color: theme.drawer.secondaryText,
            }}
          >
            {profile.userData.settings.private
              ? 'Your friends will not be able to see your updates since your profile is private'
              : 'Your friends will be able to see your updates since your profile is not private'}
          </Text>
        </Row>
        {/* Check for updates */}
        <Row>
          <Col
            xs={24}
            align='center'
            style={{
              padding: '0.5rem 0 0.5rem 0',
            }}
          >
            <Button
              block
              type='primary'
              icon={<ReloadOutlined spin={checkingVersion} />}
              loading={checkingVersion}
              onClick={handleCheckVersion}
            >
              Check for Updates
            </Button>
          </Col>
        </Row>
        {/* logout button */}
        {loggedin ? <LogoutButton /> : <LoginButton />}
        {/* Footer */}
        <Divider />
        <ProfileDrawerFooter />
      </Drawer>
    </div>
  );
};

export default ProfileDrawer;
